import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import MarkdownBody from 'components/MarkdownBody';
import Row from 'components/Common/Row';
import TextArea from 'components/Inputs/TextArea';

import { AccountKnowledgeContext } from './AccountKnowledgeProvider';
import TriggerEditor from './TriggerEditor';
import { TIER_HINTS, TIER_LABELS } from './constants';

// One account knowledge section. A staff save is LIVE — account knowledge has
// no publish step — so the editor says so rather than implying a draft state
// that doesn't exist.

const Pane = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  min-width: 0;
`;

const HeaderDiv = styled.div`
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--color-border-secondary);
`;

const FieldDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 640px;
  margin-bottom: 12px;
`;

const BodyTextArea = styled(TextArea)`
  min-height: 240px;
  font-family: monospace;
  font-size: 13px;
  width: 100%;
`;

const PreviewDiv = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 16px;
  min-height: 240px;
`;

const TierRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
`;

const TIERS: AccountKb.Tier[] = ['always', 'triggered'];

const SectionEditor = ({
  section,
  initialDraft,
  onSaved,
}: {
  // Undefined when creating: the caller owns the "new section" affordance.
  section?: AccountKb.Section;
  // A prefill for a NEW section (e.g. "Teach the agent" from a test). Used only
  // when `section` is undefined.
  initialDraft?: {
    title: string;
    summary: string;
    tier: AccountKb.Tier;
    triggers: AccountKb.Trigger[];
    body: string;
  };
  onSaved: (sectionId: string) => void;
}) => {
  const { saveSection, archiveSection, rights } = useContext(
    AccountKnowledgeContext,
  );
  const [title, setTitle] = useState<string>('');
  const [summary, setSummary] = useState<string>('');
  const [tier, setTier] = useState<AccountKb.Tier>('triggered');
  const [triggers, setTriggers] = useState<AccountKb.Trigger[]>([]);
  const [body, setBody] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);

  useEffect(() => {
    const source = section ?? initialDraft;
    setTitle(source?.title ?? '');
    setSummary(source?.summary ?? '');
    setTier(source?.tier ?? 'triggered');
    setTriggers(source?.triggers ?? []);
    setBody(section?.activeBody ?? initialDraft?.body ?? '');
    setIsPreview(false);
  }, [section, initialDraft]);

  // The same rule the server enforces, so the button explains the refusal
  // before the round trip rather than after it.
  const blockingReason = useMemo(() => {
    if (title.trim() === '') {
      return 'A section needs a title';
    }
    if (body.trim() === '') {
      return 'A section needs some content';
    }
    if (tier === 'triggered' && triggers.length === 0) {
      return 'A triggered section needs at least one trigger, or it can never reach an answer';
    }
    return undefined;
  }, [title, body, tier, triggers]);

  const onSave = async () => {
    const sectionId =
      section?.id ??
      `${title
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 48)}-${Date.now().toString(36).slice(-4)}`;
    setIsSaving(true);
    const didSave = await saveSection({
      sectionId,
      title: title.trim(),
      summary: summary.trim(),
      tier,
      triggers,
      body,
      // The revision the editor opened — the server refuses a stale save.
      ...(section
        ? { baseRev: Math.max(section.activeRev, section.proposedRev) }
        : {}),
    });
    setIsSaving(false);
    if (didSave) {
      onSaved(sectionId);
    }
  };

  return (
    <Pane data-testid="account-kb-section-editor">
      <HeaderDiv>
        <Row centerAlign spaceBetween>
          <Typography.Header type="H4">
            {section ? section.title : 'New section'}
          </Typography.Header>
          <Row centerAlign style={{ gap: 8 }}>
            <Button
              type="Ghost"
              size="Small"
              label={isPreview ? 'Edit' : 'Preview'}
              onClick={() => setIsPreview((prev) => !prev)}
            />
            {section && (
              <Button
                type="Ghost"
                size="Small"
                label="Retire"
                isDisabled={!rights.canEdit}
                onClick={() => archiveSection(section.id)}
              />
            )}
            <Button
              type="Primary"
              size="Small"
              label={isSaving ? 'Saving…' : 'Save'}
              isDisabled={
                !rights.canEdit || isSaving || Boolean(blockingReason)
              }
              isDisabledMessage={blockingReason}
              onClick={onSave}
            />
          </Row>
        </Row>
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          Saving puts this in front of the agent straight away — account
          knowledge has no separate publish step.
        </Typography.Body>
        {section?.source === 'triage' && (
          <Row centerAlign style={{ gap: 8, marginTop: 8 }}>
            <Badge badgeType="Blue" text="Drafted from feedback" />
          </Row>
        )}
      </HeaderDiv>

      <FieldDiv>
        <Typography.Body type="Label">Title</Typography.Body>
        <TextInput
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="What a load means here"
          data-testid="account-kb-title"
        />
      </FieldDiv>

      <FieldDiv>
        <Typography.Body type="Label">Summary</Typography.Body>
        <TextInput
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          placeholder="One line, for this list and the trace"
          data-testid="account-kb-summary"
        />
      </FieldDiv>

      <FieldDiv>
        <Typography.Body type="Label">When the agent sees this</Typography.Body>
        <TierRow>
          {TIERS.map((option) => (
            <Button
              key={option}
              type={tier === option ? 'Primary' : 'Secondary'}
              size="Small"
              label={TIER_LABELS[option]}
              onClick={() => setTier(option)}
              testId={`account-kb-tier-${option}`}
            />
          ))}
        </TierRow>
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          {TIER_HINTS[tier]}
        </Typography.Body>
      </FieldDiv>

      {tier === 'triggered' && (
        <FieldDiv style={{ maxWidth: 720 }}>
          <Typography.Body type="Label">Triggers</Typography.Body>
          <TriggerEditor
            triggers={triggers}
            onChange={setTriggers}
            isDisabled={!rights.canEdit}
          />
        </FieldDiv>
      )}

      <FieldDiv style={{ maxWidth: 'unset' }}>
        <Typography.Body type="Label">
          What the agent should know
        </Typography.Body>
        {isPreview ? (
          <PreviewDiv>
            <MarkdownBody>{body}</MarkdownBody>
          </PreviewDiv>
        ) : (
          <BodyTextArea
            value={body}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setBody(event.target.value)
            }
            data-testid="account-kb-body"
          />
        )}
      </FieldDiv>
    </Pane>
  );
};

export default SectionEditor;
